import React from "react";
import { useNavigate } from "react-router-dom";
import { useUiLocale } from "../../context/UiLocaleContext";
import { useWizard } from "../../context/WizardContext";
import { useTranslation } from "../../i18n/useTranslation";
import Modal from "./Modal";
import WizardLayout from "./WizardLayout";
import AdminPageNavigator from "../AdminPageNavigator";

const TOTAL_STEPS = 5;
const CURRENT_STEP = 1;

const GlobeIcon = () => (
  <svg width="82" height="82" viewBox="0 0 82 82" fill="none">
    <circle cx="41" cy="41" r="38" fill="#8DC7CD" />
    <ellipse cx="34" cy="28" rx="11" ry="8.5" fill="#5aa86a" opacity="0.88" />
    <ellipse cx="28" cy="42" rx="7.5" ry="10.5" fill="#5aa86a" opacity="0.88" />
    <ellipse cx="53" cy="34" rx="8.5" ry="11" fill="#5aa86a" opacity="0.82" />
    <ellipse cx="55" cy="52" rx="6.5" ry="8" fill="#5aa86a" opacity="0.82" />
    <ellipse
      cx="41"
      cy="41"
      rx="38"
      ry="13"
      stroke="rgba(255,255,255,0.18)"
      strokeWidth="1"
      fill="none"
    />
    <line x1="3" y1="41" x2="79" y2="41" stroke="rgba(255,255,255,0.18)" strokeWidth="1" />
    <circle cx="41" cy="41" r="38" stroke="rgba(64,89,107,0.14)" strokeWidth="1.5" />
  </svg>
);

const Step1_Language: React.FC = () => {
  const {
    language,
    setLanguage,
    createStorySession,
    isLoading,
    error,
    clearError,
  } = useWizard();
  const navigate = useNavigate();
  const { uiLocale, setUiLocale } = useUiLocale();
  const { t } = useTranslation();

  const [validationError, setValidationError] = React.useState("");
  const [showUiSwitchModal, setShowUiSwitchModal] = React.useState(false);

  const handleSelectBookLanguage = (lang: "Hebrew" | "English") => {
    const wasOther = language !== lang;
    setLanguage(lang);
    if (lang === "Hebrew" && uiLocale === "en" && wasOther) {
      setShowUiSwitchModal(true);
    }
  };

  const handleNext = async () => {
    if (!language) {
      setValidationError(t("wizard.language.selectLanguageFirst"));
      return;
    }
    setValidationError("");

    clearError();

    const success = await createStorySession();

    if (success) {
      navigate("/create/story-type");
    }
  };

  const handleBack = () => {
    navigate("/");
  };

  const handleConfirmUiHebrew = () => {
    setUiLocale("he");
    setShowUiSwitchModal(false);
  };

  return (
    <WizardLayout currentStep={CURRENT_STEP} totalSteps={TOTAL_STEPS}>
      <main
        id="main-content"
        tabIndex={-1}
        className="relative z-10 flex w-full flex-1 flex-col items-center justify-center outline-none"
      >
        <Modal
          isOpen={showUiSwitchModal}
          onClose={() => setShowUiSwitchModal(false)}
          onConfirm={handleConfirmUiHebrew}
          type="confirm"
          title={t("wizard.language.bookHebrewModalTitle")}
          message={t("wizard.language.bookHebrewModalMessage")}
          cancelText={t("wizard.language.bookHebrewModalCancel")}
          confirmText={t("wizard.language.bookHebrewModalConfirm")}
        />

        {error && (
          <div
            role="alert"
            aria-live="polite"
            className="mb-4 w-full max-w-md rounded-xl border border-red-300 bg-red-50 px-4 py-3 text-center text-red-700 shadow-soft"
          >
            <span className="block">{error}</span>
            <button
              onClick={clearError}
              className="mt-2 text-sm font-semibold text-red-700 hover:text-red-900 focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-offset-1 rounded"
            >
              {t("wizard.common.dismissError")}
            </button>
          </div>
        )}

        {validationError && (
          <div className="mb-4 w-full max-w-md rounded-xl border border-amber-300 bg-amber-50 px-4 py-3 text-center text-amber-900 shadow-soft">
            {validationError}
          </div>
        )}

        <div className="lang-card-root">
          <div className="lang-globe-wrap" aria-hidden="true">
            <GlobeIcon />
          </div>

          <p className="lang-eyebrow">{t("wizard.step", { current: CURRENT_STEP, total: TOTAL_STEPS })}</p>
          <h1 className="lang-title">{t("wizard.language.title")}</h1>
          <AdminPageNavigator align="center" />
          <p className="lang-sub">{t("wizard.language.subtitle")}</p>

          <div className="lang-grid" role="radiogroup" aria-label={t("wizard.language.title")}>
            <button
              type="button"
              role="radio"
              aria-checked={language === "English"}
              className={`lang-option${language === "English" ? " selected" : ""}`}
              onClick={() => handleSelectBookLanguage("English")}
              disabled={isLoading}
            >
              <span className="lang-check" aria-hidden="true">
                <svg width="12" height="12" viewBox="0 0 14 14" stroke="white" strokeWidth="2.8" fill="none">
                  <polyline points="2,7 6,11 12,3" />
                </svg>
              </span>
              <span className="lang-flag" aria-hidden="true">🇺🇸</span>
              <span className="lang-name">English</span>
              <span className="lang-native">English</span>
            </button>

            <button
              type="button"
              role="radio"
              aria-checked={language === "Hebrew"}
              className={`lang-option${language === "Hebrew" ? " selected" : ""}`}
              onClick={() => handleSelectBookLanguage("Hebrew")}
              disabled={isLoading}
            >
              <span className="lang-check" aria-hidden="true">
                <svg width="12" height="12" viewBox="0 0 14 14" stroke="white" strokeWidth="2.8" fill="none">
                  <polyline points="2,7 6,11 12,3" />
                </svg>
              </span>
              <span className="lang-flag" aria-hidden="true">🇮🇱</span>
              <span className="lang-name he">עברית</span>
              <span className="lang-native">Hebrew</span>
            </button>
          </div>

          {isLoading && (
            <div className="mb-4 rounded-lg bg-blue-100 p-3 text-center text-blue-800">
              {t("wizard.language.creatingSession")}
            </div>
          )}

          <div className="wizard-nav-row">
            <button type="button" className="wizard-btn-back" onClick={handleBack} disabled={isLoading}>
              ← {t("wizard.common.back")}
            </button>
            <button type="button" className="wizard-btn-next" onClick={handleNext} disabled={isLoading || !language}>
              {isLoading ? t("wizard.common.creating") : t("wizard.common.next")} →
            </button>
          </div>
        </div>
      </main>
    </WizardLayout>
  );
};

export default Step1_Language;
